import * as React from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import Swal from "sweetalert2";
import {
  Box,
  Button,
  Checkbox,
  FormControl,
  Grid,
  IconButton,
  InputAdornment,
  InputLabel,
  ListSubheader,
  ListItemText,
  MenuItem,
  OutlinedInput,
  Paper,
  Popover,
  Select,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { store } from "../../../../../store";
import {
  createAnalysis,
  deleteAnalysis,
  getAnalysis,
  updateAnalysis,
} from "../../../../../redux/actions/analysis";
import { getMaterials } from "../../../../../redux/actions/material";
import {
  closeLoadingAlert,
  showErrorAlert,
  showLoadingAlert,
} from "../../../../ui/utils/AlertUtils";
import { getTemperaturesByStudy } from "../../../../../services/temperatureService";
import { getSampleSetupItems } from "../../../../../services/sampleSetupService";
import { renderAnalysisContainerLabel } from "../../../../ui/AnalysisContainerLabel";
import {
  ANALYSIS_COLORS,
  DEFAULT_ANALYSIS_COLOR,
  getAnalysisColorTitle,
  normalizeAnalysisColor,
} from "./AnalysisColorPalette";

const MULTI_SELECT_MENU_PROPS = {
  anchorOrigin: { vertical: "bottom", horizontal: "left" },
  transformOrigin: { vertical: "top", horizontal: "left" },
  PaperProps: {
    sx: {
      width: { xs: "calc(100vw - 64px)", sm: 520 },
      minWidth: "0 !important",
      maxWidth: "calc(100vw - 64px)",
    },
  },
};

const NO_COLOR_SWATCH_SX = {
  width: 40,
  height: 40,
  bgcolor: "grey.400",
  "&:hover": { bgcolor: "grey.500" },
  "&::after": {
    content: '""',
    width: 22,
    height: 3,
    borderRadius: 1,
    bgcolor: "common.white",
    transform: "rotate(-45deg)",
  },
};

const getMaterialLabel = (material = {}) => material.name || "";

const getRecordId = (record = {}) =>
  record.id ?? record.value ?? record.temperatureId ?? "";

const normalizeIdValue = (value) => {
  const normalizedValue = Number(value);
  return Number.isInteger(normalizedValue) && normalizedValue > 0
    ? normalizedValue
    : null;
};

const findRecordByName = (records, name) =>
  records.find(
    (record) => String(record.name || "").trim() === String(name || "").trim(),
  );

const normalizeQuantityValue = (value) => {
  const normalizedValue = Number(value);

  return Number.isSafeInteger(normalizedValue) && normalizedValue > 0
    ? normalizedValue
    : null;
};

const parseStoredVisitQuantities = (rawQuantity) => {
  if (rawQuantity === null || rawQuantity === undefined) {
    return null;
  }

  let quantity = rawQuantity;
  if (typeof quantity === "string") {
    try {
      quantity = JSON.parse(quantity);
    } catch (_error) {
      return null;
    }
  }

  if (!Array.isArray(quantity)) {
    return null;
  }

  return quantity.reduce((visitQuantities, entry) => {
    const visitId = normalizeIdValue(entry?.visitId);
    if (!visitId) {
      return visitQuantities;
    }

    const sampleTypes = Object.entries(entry.sampleTypes || {}).reduce(
      (quantities, [sampleTypeId, value]) => {
        const normalizedSampleTypeId = normalizeIdValue(sampleTypeId);
        const normalizedValue = normalizeQuantityValue(value);

        if (normalizedSampleTypeId && normalizedValue !== null) {
          quantities[String(normalizedSampleTypeId)] = normalizedValue;
        }

        return quantities;
      },
      {},
    );
    const defaultQuantity = normalizeQuantityValue(entry.default);

    visitQuantities[String(visitId)] = {
      default: defaultQuantity ?? undefined,
      sampleTypes,
    };

    return visitQuantities;
  }, {});
};

const buildVisitQuantityState = (
  rawQuantity,
  analysisVisits,
  analysisSampleTypes,
) => {
  const storedVisitQuantities = parseStoredVisitQuantities(rawQuantity);
  if (storedVisitQuantities !== null) {
    return storedVisitQuantities;
  }

  const legacySampleTypeIds = (analysisSampleTypes || [])
    .map((sampleType) => normalizeIdValue(sampleType.id))
    .filter(Boolean);

  return (analysisVisits || []).reduce((visitQuantities, visit) => {
    const visitId = normalizeIdValue(visit.id);
    if (!visitId) {
      return visitQuantities;
    }

    visitQuantities[String(visitId)] = {
      default: legacySampleTypeIds.length ? undefined : 1,
      sampleTypes: Object.fromEntries(
        legacySampleTypeIds.map((sampleTypeId) => [String(sampleTypeId), 1]),
      ),
    };

    return visitQuantities;
  }, {});
};

const serializeVisitQuantities = (
  visitOptions,
  selectedVisitIds,
  selectedSampleTypeIds,
  visitQuantities,
) => {
  const selectedVisitIdSet = new Set(
    selectedVisitIds.map((visitId) => Number(visitId)),
  );

  return visitOptions.flatMap((visit) => {
    const visitId = normalizeIdValue(getRecordId(visit));
    if (!visitId || !selectedVisitIdSet.has(visitId)) {
      return [];
    }

    const visitQuantity = visitQuantities[String(visitId)] || {};
    const defaultQuantity = selectedSampleTypeIds.length
      ? 0
      : normalizeQuantityValue(visitQuantity.default) ?? 1;
    const sampleTypes = Object.fromEntries(
      selectedSampleTypeIds.map((sampleTypeId) => [
        String(sampleTypeId),
        normalizeQuantityValue(
          visitQuantity.sampleTypes?.[String(sampleTypeId)],
        ) ?? 1,
      ]),
    );
    return [
      {
        visitId,
        default: defaultQuantity,
        sampleTypes,
      },
    ];
  });
};

export const ScreenAnalysisForm = ({
  analysisId: analysisIdProp,
  studyContext,
  embedded = false,
  onClose,
  onSaved,
} = {}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const params = useParams();
  const analysisId = analysisIdProp ?? params.id;
  const isEditMode = analysisId && analysisId !== "new";
  const selectedStudyId = studyContext?.studyId ?? location.state?.studyId;
  const selectedStudyCode =
    studyContext?.studyCode ?? location.state?.studyCode;
  const selectedCenterNumber =
    studyContext?.centerNumber ?? location.state?.centerNumber;
  const selectedCenterId = studyContext?.centerId ?? location.state?.centerId;
  const analysis = useSelector((state) => state.analysis.analysis);
  const materials = useSelector((state) => state.material.materials);
  const [name, setName] = React.useState("");
  const [color, setColor] = React.useState(DEFAULT_ANALYSIS_COLOR);
  // const [reference, setReference] = React.useState("");
  const [description, setDescription] = React.useState("");
  const [temperature, setTemperature] = React.useState("");
  const [temperatureId, setTemperatureId] = React.useState("");
  const [temperatureRecords, setTemperatureRecords] = React.useState([]);
  const [temperatureOptions, setTemperatureOptions] = React.useState([]);
  const [areTemperatureOptionsLoaded, setAreTemperatureOptionsLoaded] =
    React.useState(false);
  const [analysisContainerId, setAnalysisContainerId] = React.useState("");
  const [analysisContainerOptions, setAnalysisContainerOptions] =
    React.useState([]);
  const [visitIds, setVisitIds] = React.useState([]);
  const [visitQuantities, setVisitQuantities] = React.useState({});
  const [visitOptions, setVisitOptions] = React.useState([]);
  const [sampleTypeIds, setSampleTypeIds] = React.useState([]);
  const [sampleTypeOptions, setSampleTypeOptions] = React.useState([]);
  const [materialIds, setMaterialIds] = React.useState([]);
  const [colorPickerAnchor, setColorPickerAnchor] = React.useState(null);
  const [isSaving, setIsSaving] = React.useState(false);
  const [errorMessage, setErrorMessage] = React.useState("");
  const effectiveStudyId = selectedStudyId || analysis?.studyId;
  const FormFrame = embedded ? Box : Paper;

  const routeState = React.useMemo(
    () => ({
      studyId: selectedStudyId,
      studyCode: selectedStudyCode,
      centerNumber: selectedCenterNumber,
      centerId: selectedCenterId,
    }),
    [
      selectedCenterId,
      selectedCenterNumber,
      selectedStudyCode,
      selectedStudyId,
    ],
  );

  React.useEffect(() => {
    if (isEditMode) {
      store.dispatch(getAnalysis(analysisId));
    }
  }, [analysisId, isEditMode]);

  React.useEffect(() => {
    if (effectiveStudyId) {
      store.dispatch(getMaterials(effectiveStudyId));
    }
  }, [effectiveStudyId]);

  React.useEffect(() => {
    let isMounted = true;

    const loadSampleTypes = async () => {
      if (!effectiveStudyId) {
        setSampleTypeOptions([]);
        return;
      }

      try {
        const studySampleTypes = await getSampleSetupItems(
          "sampleTypes",
          effectiveStudyId,
        );

        if (isMounted) {
          setSampleTypeOptions(
            [...(studySampleTypes || [])].sort((left, right) =>
              String(left.name || "").localeCompare(String(right.name || "")),
            ),
          );
        }
      } catch (error) {
        console.error("Failed to load study sample types:", error);
        if (isMounted) {
          setSampleTypeOptions([]);
        }
      }
    };

    loadSampleTypes();

    return () => {
      isMounted = false;
    };
  }, [effectiveStudyId]);

  React.useEffect(() => {
    let isMounted = true;

    const loadVisits = async () => {
      if (!effectiveStudyId) {
        setVisitOptions([]);
        return;
      }

      try {
        const studyVisits = await getSampleSetupItems(
          "visits",
          effectiveStudyId,
        );

        if (isMounted) {
          setVisitOptions(
            [...(studyVisits || [])].sort((left, right) =>
              String(left.name || "").localeCompare(String(right.name || "")),
            ),
          );
        }
      } catch (error) {
        console.error("Failed to load study visits:", error);
        if (isMounted) {
          setVisitOptions([]);
        }
      }
    };

    loadVisits();

    return () => {
      isMounted = false;
    };
  }, [effectiveStudyId]);

  React.useEffect(() => {
    let isMounted = true;

    const loadTemperatures = async () => {
      setAreTemperatureOptionsLoaded(false);

      if (!effectiveStudyId) {
        setTemperatureRecords([]);
        setTemperatureOptions([]);
        setAreTemperatureOptionsLoaded(true);
        return;
      }

      try {
        const studyTemperatures =
          await getTemperaturesByStudy(effectiveStudyId);
        const nextOptions = (studyTemperatures || [])
          .map((studyTemperature) => studyTemperature.name)
          .filter(Boolean)
          .sort((left, right) => left.localeCompare(right));

        if (isMounted) {
          setTemperatureRecords(studyTemperatures || []);
          setTemperatureOptions(nextOptions);
          setAreTemperatureOptionsLoaded(true);
        }
      } catch (error) {
        console.error("Failed to load study temperatures:", error);
        if (isMounted) {
          setTemperatureRecords([]);
          setTemperatureOptions([]);
          setAreTemperatureOptionsLoaded(true);
        }
      }
    };

    loadTemperatures();

    return () => {
      isMounted = false;
    };
  }, [effectiveStudyId]);

  React.useEffect(() => {
    let isMounted = true;

    const loadAnalysisContainers = async () => {
      if (!effectiveStudyId) {
        setAnalysisContainerOptions([]);
        return;
      }

      try {
        const studyAnalysisContainers = await getSampleSetupItems(
          "analysisContainers",
          effectiveStudyId,
        );

        if (isMounted) {
          setAnalysisContainerOptions(
            [...(studyAnalysisContainers || [])].sort((left, right) =>
              String(left.name || "").localeCompare(String(right.name || "")),
            ),
          );
        }
      } catch (error) {
        console.error("Failed to load study analysis containers:", error);
        if (isMounted) {
          setAnalysisContainerOptions([]);
        }
      }
    };

    loadAnalysisContainers();

    return () => {
      isMounted = false;
    };
  }, [effectiveStudyId]);

  const availableTemperatureOptions = temperatureOptions;

  React.useEffect(() => {
    if (
      areTemperatureOptionsLoaded &&
      temperature &&
      !availableTemperatureOptions.includes(temperature)
    ) {
      setTemperature("");
      setTemperatureId("");
    }
  }, [
    areTemperatureOptionsLoaded,
    availableTemperatureOptions,
    temperature,
  ]);

  React.useEffect(() => {
    if (isEditMode && analysis && String(analysis.id) === String(analysisId)) {
      const analysisTemperatureId =
        analysis.temperatureId ??
        analysis.temperatureDefinition?.id ??
        "";
      const analysisContainerId =
        analysis.analysisContainerId ?? analysis.analysisContainer?.id ??
        "";

      setName(analysis.name || "");
      setColor(
        normalizeAnalysisColor(analysis.color, DEFAULT_ANALYSIS_COLOR),
      );
      // setReference(analysis.reference || "");
      setDescription(analysis.description || "");
      setTemperature(
        analysis.temperatureDefinition?.name || analysis.temperature || "",
      );
      setTemperatureId(
        analysisTemperatureId ? String(analysisTemperatureId) : "",
      );
      setAnalysisContainerId(
        analysisContainerId ? String(analysisContainerId) : "",
      );
      const analysisVisits = analysis.visits || analysis.Visits || [];
      const analysisSampleTypes = analysis.sampleTypes || [];

      setVisitIds(analysisVisits.map((visit) => Number(visit.id)));
      setVisitQuantities(
        buildVisitQuantityState(
          analysis.quantity,
          analysisVisits,
          analysisSampleTypes,
        ),
      );
      setSampleTypeIds(
        analysisSampleTypes.map((sampleType) => Number(sampleType.id)),
      );
      setMaterialIds(
        (analysis.materials || analysis.Materials || []).map((material) =>
          Number(material.id),
        ),
      );
    }
  }, [analysis, analysisId, isEditMode]);

  React.useEffect(() => {
    if (!isEditMode) {
      setName("");
      setColor(DEFAULT_ANALYSIS_COLOR);
      // setReference("");
      setDescription("");
      setTemperature("");
      setTemperatureId("");
      setAnalysisContainerId("");
      setVisitIds([]);
      setVisitQuantities({});
      setSampleTypeIds([]);
      setMaterialIds([]);
    }
  }, [isEditMode]);

  React.useEffect(() => {
    if (temperatureId || !temperature || !temperatureRecords.length) {
      return;
    }

    const matchingTemperature = findRecordByName(
      temperatureRecords,
      temperature,
    );

    if (matchingTemperature?.id) {
      setTemperatureId(String(matchingTemperature.id));
    }
  }, [temperature, temperatureId, temperatureRecords]);

  React.useEffect(() => {
    if (
      !isEditMode &&
      availableTemperatureOptions.length &&
      (!temperature || !availableTemperatureOptions.includes(temperature))
    ) {
      const nextTemperature = availableTemperatureOptions[0];
      const matchingTemperature = findRecordByName(
        temperatureRecords,
        nextTemperature,
      );

      setTemperature(nextTemperature);
      setTemperatureId(
        matchingTemperature?.id ? String(matchingTemperature.id) : "",
      );
    }
  }, [
    availableTemperatureOptions,
    isEditMode,
    temperature,
    temperatureRecords,
  ]);

  const materialOptions = React.useMemo(
    () =>
      [...(materials || [])]
        .filter(
          (material) =>
            !effectiveStudyId ||
            !material.studyId ||
            String(material.studyId) === String(effectiveStudyId),
        )
        .filter((material) => material.type === "material")
        .sort((firstMaterial, secondMaterial) =>
          getMaterialLabel(firstMaterial).localeCompare(
            getMaterialLabel(secondMaterial),
          ),
        ),
    [effectiveStudyId, materials],
  );

  React.useEffect(() => {
    if (!embedded && !selectedStudyId && !isEditMode) {
      navigate("/samples", { replace: true, state: routeState });
    }
  }, [embedded, isEditMode, navigate, routeState, selectedStudyId]);

  const closeForm = React.useCallback(() => {
    if (onClose) {
      onClose();
      return;
    }

    navigate("/sample/analysis", { state: routeState });
  }, [navigate, onClose, routeState]);

  const completeForm = React.useCallback(async () => {
    if (onSaved) {
      await onSaved();
      return;
    }

    navigate("/sample/analysis", { state: routeState, replace: true });
  }, [navigate, onSaved, routeState]);

  const handleSampleTypeChange = (event) => {
    const rawValue = event.target.value;
    const nextSampleTypeIds = (
      typeof rawValue === "string" ? rawValue.split(",") : rawValue
    ).map((value) => Number(value));
    const nextSampleTypeIdSet = new Set(
      nextSampleTypeIds.map((sampleTypeId) => String(sampleTypeId)),
    );

    setSampleTypeIds(nextSampleTypeIds);
    setVisitQuantities((currentVisitQuantities) =>
      Object.fromEntries(
        Object.entries(currentVisitQuantities).map(
          ([visitId, visitQuantity]) => [
            visitId,
            {
              ...visitQuantity,
              default: nextSampleTypeIds.length
                ? undefined
                : visitQuantity.default,
              sampleTypes: Object.fromEntries(
                Object.entries(visitQuantity.sampleTypes || {}).filter(
                  ([sampleTypeId]) => nextSampleTypeIdSet.has(sampleTypeId),
                ),
              ),
            },
          ],
        ),
      ),
    );
  };

  const handleVisitChange = (event) => {
    const rawValue = event.target.value;
    const nextVisitIds = (
      typeof rawValue === "string" ? rawValue.split(",") : rawValue
    ).map((value) => Number(value));
    const nextVisitIdSet = new Set(
      nextVisitIds.map((visitId) => String(visitId)),
    );

    setVisitIds(nextVisitIds);
    setVisitQuantities((currentVisitQuantities) =>
      Object.fromEntries(
        Object.entries(currentVisitQuantities).filter(([visitId]) =>
          nextVisitIdSet.has(visitId),
        ),
      ),
    );
  };

  const handleVisitQuantityChange = (visitId, sampleTypeId, rawValue) => {
    const quantity = rawValue === "" ? 1 : normalizeQuantityValue(rawValue);
    if (quantity === null) {
      return;
    }

    setVisitQuantities((currentVisitQuantities) => {
      const visitKey = String(visitId);
      const nextVisitQuantity = {
        ...(currentVisitQuantities[visitKey] || {}),
        sampleTypes: {
          ...(currentVisitQuantities[visitKey]?.sampleTypes || {}),
        },
      };

      if (sampleTypeId === null) {
        nextVisitQuantity.default = quantity;
      } else if (quantity === undefined) {
        delete nextVisitQuantity.sampleTypes[String(sampleTypeId)];
      } else {
        nextVisitQuantity.sampleTypes[String(sampleTypeId)] = quantity;
      }

      return {
        ...currentVisitQuantities,
        [visitKey]: nextVisitQuantity,
      };
    });
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    const resolvedStudyId = effectiveStudyId;
    if (!resolvedStudyId) {
      setErrorMessage(t("missingStudy", "The study context is missing."));
      return;
    }

    if (!name.trim() || !temperature) {
      setErrorMessage(t("pleaseFillAllRequiredFields"));
      return;
    }

    const quantity = serializeVisitQuantities(
      visitOptions,
      visitIds,
      sampleTypeIds,
      visitQuantities,
    );
    const payload = {
      name: name.trim(),
      color,
      // reference: reference.trim() || null,
      description: description?.trim() || null,
      temperature,
      temperatureId: normalizeIdValue(temperatureId),
      analysisContainerId: normalizeIdValue(analysisContainerId),
      studyId: resolvedStudyId,
      quantity,
      visitIds,
      sampleTypeIds,
      materialIds,
    };

    setIsSaving(true);
    setErrorMessage("");

    try {
      showLoadingAlert(
        isEditMode
          ? t("updatingAnalysis", "Updating analysis...")
          : t("creatingAnalysis", "Creating analysis..."),
      );

      if (isEditMode) {
        await store.dispatch(updateAnalysis({ id: analysisId, ...payload }));
      } else {
        await store.dispatch(createAnalysis(payload));
      }

      closeLoadingAlert();
      await completeForm();
    } catch (error) {
      closeLoadingAlert();
      console.error("Failed to save analysis:", error);
      setErrorMessage(
        isEditMode
          ? t("failedToUpdateAnalysis", "Failed to update analysis")
          : t("failedToCreateAnalysis", "Failed to create analysis"),
      );
      showErrorAlert(
        isEditMode
          ? t("failedToUpdateAnalysis", "Failed to update analysis")
          : t("failedToCreateAnalysis", "Failed to create analysis"),
      );
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!isEditMode) {
      return;
    }

    if (embedded) {
      onClose?.();
      await new Promise((resolve) => setTimeout(resolve, 0));
    }

    const result = await Swal.fire({
      text: t(
        "confirmDeleteAnalysis",
        "Are you sure you want to delete this analysis?",
      ),
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: t("delete", "Delete"),
      cancelButtonText: t("cancel", "Cancel"),
      confirmButtonColor: "#d32f2f",
      reverseButtons: true,
    });

    if (!result.isConfirmed) {
      return;
    }

    setIsSaving(true);
    setErrorMessage("");

    try {
      showLoadingAlert(t("deletingAnalysis", "Deleting analysis..."));
      await store.dispatch(deleteAnalysis(analysisId));
      closeLoadingAlert();
      await completeForm();
    } catch (error) {
      closeLoadingAlert();
      console.error("Failed to delete analysis:", error);
      setErrorMessage(t("failedToDeleteAnalysis", "Failed to delete analysis"));
      showErrorAlert(t("failedToDeleteAnalysis", "Failed to delete analysis"));
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Grid
      container
      justifyContent="center"
      sx={embedded ? { py: 0 } : { pb: 2 }}>
      <Grid item xs={12} md={embedded ? 12 : 8} lg={embedded ? 12 : 6}>
        <FormFrame
          component="form"
          onSubmit={handleSubmit}
          sx={embedded ? { pt: 1 } : { p: 4, borderRadius: 3 }}>
          {!embedded && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="h5">
                {isEditMode
                  ? t("editAnalysis", "Edit analysis")
                  : t("createAnalysis")}
              </Typography>
            </Box>
          )}

          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                required
                label={t("name")}
                value={name}
                onChange={(event) => setName(event.target.value)}
                sx={{
                  "& .MuiInputBase-input": {
                    color: color || "text.primary",
                    textShadow: "0 0 1px rgba(0, 0, 0, 0.6)",
                    fontWeight: color ? 600 : 400,
                  },
                }}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end" sx={{ ml: 1 }}>
                      <Tooltip
                        title={
                          color
                            ? getAnalysisColorTitle(color)
                            : t("none", "None")
                        }
                        arrow>
                        <IconButton
                          aria-label={t("color", "Color")}
                          aria-haspopup="dialog"
                          aria-expanded={Boolean(colorPickerAnchor)}
                          onClick={(event) =>
                            setColorPickerAnchor(event.currentTarget)
                          }
                          sx={
                            color
                              ? {
                                  width: 40,
                                  height: 40,
                                  bgcolor: color,
                                  border: "2px solid",
                                  borderColor: "divider",
                                  "&:hover": { bgcolor: color },
                                }
                              : {
                                  ...NO_COLOR_SWATCH_SX,
                                  border: "2px solid",
                                  borderColor: "divider",
                                }
                          }
                        />
                      </Tooltip>
                    </InputAdornment>
                  ),
                }}
              />
              <Popover
                open={Boolean(colorPickerAnchor)}
                anchorEl={colorPickerAnchor}
                onClose={() => setColorPickerAnchor(null)}
                anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                transformOrigin={{ vertical: "top", horizontal: "right" }}>
                <Box
                  role="dialog"
                  aria-label={t("color", "Color")}
                  sx={{ p: 1.5 }}>
                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: "repeat(5, 40px)",
                      gap: 1,
                    }}>
                    {ANALYSIS_COLORS.map((colorOption) => (
                      <Tooltip
                        key={colorOption.color}
                        title={colorOption.title}
                        arrow>
                        <IconButton
                          aria-label={colorOption.title}
                          aria-pressed={color === colorOption.color}
                          onClick={() => {
                            setColor(colorOption.color);
                            setColorPickerAnchor(null);
                          }}
                          sx={{
                            width: 40,
                            height: 40,
                            bgcolor: colorOption.color,
                            border: "3px solid",
                            borderColor:
                              color === colorOption.color
                                ? "common.white"
                                : "transparent",
                            boxShadow:
                              color === colorOption.color
                                ? "0 0 0 2px rgba(0, 0, 0, 0.45)"
                                : "none",
                            "&:hover": { bgcolor: colorOption.color },
                          }}
                        />
                      </Tooltip>
                    ))}
                    <Tooltip title={t("none", "None")} arrow>
                      <IconButton
                        aria-label={t("none", "None")}
                        aria-pressed={!color}
                        onClick={() => {
                          setColor(null);
                          setColorPickerAnchor(null);
                        }}
                        sx={{
                          ...NO_COLOR_SWATCH_SX,
                          border: "3px solid",
                          borderColor: !color
                            ? "common.white"
                            : "transparent",
                          boxShadow: !color
                            ? "0 0 0 2px rgba(0, 0, 0, 0.45)"
                            : "none",
                        }}
                      />
                    </Tooltip>
                  </Box>
                </Box>
              </Popover>
            </Grid>
            {/* <Grid item xs={12}>
              <TextField
                fullWidth
                label={t("reference", "Reference")}
                value={reference}
                onChange={(event) => setReference(event.target.value)}
              />
            </Grid> */}
            {/* The description is intentionally retained in the data model but
                hidden from the analysis form.
            <Grid item xs={12}>
              <TextField
                fullWidth
                label={t("description")}
                value={description}
                onChange={(event) => setDescription(event.target.value)}
                multiline
                minRows={3}
              />
            </Grid>
            */}
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel id="sample-types-label">
                  {t("primaryBackupAbbreviation", "PRIM / BU")}
                </InputLabel>
                <Select
                  labelId="sample-types-label"
                  multiple
                  value={sampleTypeIds}
                  MenuProps={MULTI_SELECT_MENU_PROPS}
                  onChange={handleSampleTypeChange}
                  input={
                    <OutlinedInput
                      label={t("primaryBackupAbbreviation", "PRIM / BU")}
                    />
                  }
                  renderValue={(selected) => {
                    const selectedIds = new Set(
                      selected.map((sampleTypeId) => Number(sampleTypeId)),
                    );

                    return sampleTypeOptions
                      .filter((sampleType) =>
                        selectedIds.has(Number(getRecordId(sampleType))),
                      )
                      .map((sampleType) => sampleType.name)
                      .join(", ");
                  }}>
                  {sampleTypeOptions.map((sampleType) => {
                    const sampleTypeId = Number(getRecordId(sampleType));

                    return (
                      <MenuItem key={sampleTypeId} value={sampleTypeId}>
                        <Checkbox
                          checked={sampleTypeIds.includes(sampleTypeId)}
                        />
                        <ListItemText primary={sampleType.name} />
                      </MenuItem>
                    );
                  })}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel id="analysis-visits-label">
                  {t("visits", "Visits")}
                </InputLabel>
                <Select
                  labelId="analysis-visits-label"
                  multiple
                  value={visitIds}
                  onChange={handleVisitChange}
                  input={<OutlinedInput label={t("visits", "Visits")} />}
                  renderValue={(selected) => {
                    const selectedIds = new Set(
                      selected.map((visitId) => Number(visitId)),
                    );

                    return visitOptions
                      .filter((visit) =>
                        selectedIds.has(Number(getRecordId(visit))),
                      )
                      .map((visit) => visit.name)
                      .join(", ");
                  }}
                  MenuProps={{
                    ...MULTI_SELECT_MENU_PROPS,
                    PaperProps: {
                      sx: {
                        ...MULTI_SELECT_MENU_PROPS.PaperProps.sx,
                        width: {
                          xs: "calc(100vw - 32px)",
                          sm: Math.max(
                            620,
                            340 + Math.max(sampleTypeIds.length, 1) * 96,
                          ),
                        },
                        maxWidth: "calc(100vw - 32px)",
                        overflowX: "auto",
                      },
                    },
                    MenuListProps: {
                      sx: {
                        minWidth: Math.max(
                          580,
                          320 + Math.max(sampleTypeIds.length, 1) * 88,
                        ),
                      },
                    },
                  }}>
                  <ListSubheader
                    sx={{
                      py: 1,
                      lineHeight: 1.2,
                      top: 0,
                      zIndex: 1,
                      bgcolor: "background.paper",
                      borderBottom: 1,
                      borderColor: "divider",
                    }}>
                    <Box
                      sx={{
                        display: "grid",
                        gridTemplateColumns: `minmax(280px, 1fr) repeat(${Math.max(
                          sampleTypeIds.length,
                          1,
                        )}, 80px)`,
                        alignItems: "center",
                        gap: 1,
                        fontWeight: 700,
                      }}>
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "baseline",
                          justifyContent: "space-between",
                          gap: 2,
                          pr: 1,
                          whiteSpace: "nowrap",
                        }}>
                        <Box>{t("visits", "Visits")}</Box>
                        <Typography
                          component="span"
                          variant="caption"
                          sx={{
                            fontStyle: "italic",
                            fontWeight: 400,
                            lineHeight: 1.2,
                          }}>
                          {t("visitQuantityHint", "Quantity of samples")}
                        </Typography>
                      </Box>
                      {sampleTypeIds.length ? (
                        sampleTypeOptions
                          .filter((sampleType) =>
                            sampleTypeIds.includes(
                              Number(getRecordId(sampleType)),
                            ),
                          )
                          .map((sampleType) => (
                            <Box key={getRecordId(sampleType)} textAlign="center">
                              {sampleType.name}
                            </Box>
                          ))
                      ) : (
                        <Box textAlign="center">
                          {t("default", "Default").toUpperCase()}
                        </Box>
                      )}
                    </Box>
                  </ListSubheader>
                  {visitOptions.map((visit) => {
                    const visitId = Number(getRecordId(visit));
                    const visitQuantity =
                      visitQuantities[String(visitId)] || {};
                    const isSelected = visitIds.includes(visitId);

                    return (
                      <MenuItem
                        key={visitId}
                        value={visitId}
                        disableRipple
                        sx={{ py: 0.75 }}>
                        <Box
                          sx={{
                            width: "100%",
                            display: "grid",
                            gridTemplateColumns: `minmax(280px, 1fr) repeat(${Math.max(
                              sampleTypeIds.length,
                              1,
                            )}, 80px)`,
                            alignItems: "center",
                            gap: 1,
                          }}>
                          <Box sx={{ display: "flex", alignItems: "center" }}>
                            <Checkbox checked={isSelected} />
                            <ListItemText primary={visit.name} />
                          </Box>
                          {isSelected && sampleTypeIds.length ? (
                            sampleTypeOptions
                              .filter((sampleType) =>
                                sampleTypeIds.includes(
                                  Number(getRecordId(sampleType)),
                                ),
                              )
                              .map((sampleType) => {
                                const sampleTypeId = Number(
                                  getRecordId(sampleType),
                                );

                                return (
                                  <TextField
                                    key={sampleTypeId}
                                    type="number"
                                    size="small"
                                    value={
                                      normalizeQuantityValue(
                                        visitQuantity.sampleTypes?.[
                                          String(sampleTypeId)
                                        ],
                                      ) ?? 1
                                    }
                                    onMouseDown={(event) =>
                                      event.stopPropagation()
                                    }
                                    onClick={(event) => event.stopPropagation()}
                                    onKeyDown={(event) => event.stopPropagation()}
                                    onChange={(event) =>
                                      handleVisitQuantityChange(
                                        visitId,
                                        sampleTypeId,
                                        event.target.value,
                                      )
                                    }
                                    inputProps={{
                                      min: 1,
                                      step: 1,
                                      "aria-label": `${visit.name} ${sampleType.name} ${t(
                                        "quantity",
                                        "Quantity",
                                      )}`,
                                    }}
                                    sx={{
                                      "& input": { textAlign: "left" },
                                    }}
                                  />
                                );
                              })
                          ) : isSelected ? (
                            <TextField
                              type="number"
                              size="small"
                              value={
                                normalizeQuantityValue(
                                  visitQuantity.default,
                                ) ?? 1
                              }
                              onMouseDown={(event) => event.stopPropagation()}
                              onClick={(event) => event.stopPropagation()}
                              onKeyDown={(event) => event.stopPropagation()}
                              onChange={(event) =>
                                handleVisitQuantityChange(
                                  visitId,
                                  null,
                                  event.target.value,
                                )
                              }
                              inputProps={{
                                min: 1,
                                step: 1,
                                "aria-label": `${visit.name} ${t(
                                  "default",
                                  "Default",
                                )} ${t("quantity", "Quantity")}`,
                              }}
                              sx={{ "& input": { textAlign: "left" } }}
                            />
                          ) : (
                            <Box
                              sx={{
                                gridColumn: `span ${Math.max(
                                  sampleTypeIds.length,
                                  1,
                                )}`,
                              }}
                            />
                          )}
                        </Box>
                      </MenuItem>
                    );
                  })}
                  {!visitOptions.length && (
                    <MenuItem disabled>
                      {t("noVisitsAvailable", "No visits available")}
                    </MenuItem>
                  )}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth required>
                <InputLabel id="analysis-temperature-label">
                  {t("temperature", "Temperature")}
                </InputLabel>
                <Select
                  labelId="analysis-temperature-label"
                  label={t("temperature", "Temperature")}
                  value={temperature}
                  onChange={(event) => {
                    const nextTemperature = event.target.value;
                    const matchingTemperature = findRecordByName(
                      temperatureRecords,
                      nextTemperature,
                    );

                    setTemperature(nextTemperature);
                    setTemperatureId(
                      matchingTemperature?.id
                        ? String(matchingTemperature.id)
                        : "",
                    );
                  }}>
                  {availableTemperatureOptions.map((option) => (
                    <MenuItem key={option} value={option}>
                      {option}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel id="analysis-container-label">
                  {t("analysisContainer", "Container")}
                </InputLabel>
                <Select
                  labelId="analysis-container-label"
                  label={t("analysisContainer", "Container")}
                  value={analysisContainerId}
                  renderValue={(selectedId) =>
                    renderAnalysisContainerLabel(
                      analysisContainerOptions.find(
                        (container) =>
                          String(getRecordId(container)) === String(selectedId),
                      ),
                    )
                  }
                  onChange={(event) =>
                    setAnalysisContainerId(event.target.value)
                  }>
                  <MenuItem value="">
                    <em>{t("none", "None")}</em>
                  </MenuItem>
                  {analysisContainerOptions.map((container) => (
                    <MenuItem
                      key={getRecordId(container)}
                      value={String(getRecordId(container))}>
                      {renderAnalysisContainerLabel(container)}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            {/* Material selection is intentionally hidden from the analysis form.
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel id="analysis-materials-label">
                  {t("materials", "Materials")}
                </InputLabel>
                <Select
                  labelId="analysis-materials-label"
                  multiple
                  value={materialIds}
                  onChange={(event) => {
                    const nextValue = event.target.value;
                    setMaterialIds(
                      (typeof nextValue === "string"
                        ? nextValue.split(",")
                        : nextValue
                      ).map((value) => Number(value)),
                    );
                  }}
                  input={<OutlinedInput label={t("materials", "Materials")} />}
                  renderValue={(selected) =>
                    materialOptions
                      .filter((material) =>
                        selected.includes(Number(material.id)),
                      )
                      .map(getMaterialLabel)
                      .join(", ")
                  }>
                  {materialOptions.map((material) => (
                    <MenuItem key={material.id} value={material.id}>
                      <Checkbox
                        checked={materialIds.includes(Number(material.id))}
                      />
                      <ListItemText primary={getMaterialLabel(material)} />
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            */}
            <Grid item xs={12}>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  gap: 2,
                }}>
                <Box>
                  {isEditMode && (
                    <Button
                      type="button"
                      variant="outlined"
                      color="error"
                      disabled={isSaving}
                      onClick={handleDelete}>
                      {t("delete", "Delete")}
                    </Button>
                  )}
                </Box>
                <Box sx={{ display: "flex", gap: 2 }}>
                  <Button variant="text" onClick={closeForm}>
                    {t("cancel")}
                  </Button>
                  <Button type="submit" variant="contained" disabled={isSaving}>
                    {isEditMode ? t("update") : t("create")}
                  </Button>
                </Box>
              </Box>
            </Grid>
          </Grid>

          {errorMessage && (
            <Box
              sx={{
                mt: 2,
                color: "#721c24",
                backgroundColor: "#f8d7da",
                borderColor: "#f5c6cb",
                p: 1.5,
                border: "1px solid transparent",
                borderRadius: 1,
              }}>
              {errorMessage}
            </Box>
          )}
        </FormFrame>
      </Grid>
    </Grid>
  );
};
